import React from "react";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

const FilteredPaymentsReport = ({
  payments = [],
  companyInfo = {
    name: "Your Company Name",
    address: "123, Avenue Mohammed V, Casablanca",
    phone: "+212 5XX-XXXXXX",
    email: "contact@company.com",
  },
  filterInfo = {
    client: null,
    dateRange: { start: "", end: "" },
  },
}) => {
  const [isPrinting, setIsPrinting] = React.useState(false);

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const totalAmount = payments.reduce(
    (sum, payment) => sum + parseFloat(payment.montant || 0),
    0
  );

  const handlePrint = async () => {
    setIsPrinting(true);
    try {
      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        throw new Error(
          "Unable to open print window. Please check if pop-ups are blocked."
        );
      }

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Rapport des Paiements Filtrés</title>
            <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600;700&display=swap">
            <style>
              @page {
                size: A4;
                margin: 20mm;
              }
              
              * {
                box-sizing: border-box;
                margin: 0;
                padding: 0;
              }

              body {
                font-family: 'Open Sans', Arial, sans-serif;
                line-height: 1.6;
                color: #2c3e50;
                background: #fff;
                font-size: 12px;
              }

              .page {
                position: relative;
                width: 210mm;
                min-height: 297mm;
                padding: 20mm;
                margin: 0 auto;
                background: white;
              }

              /* Professional Header Styling */
              .company-header {
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                margin-bottom: 40px;
              }

              .company-info {
                flex: 1;
              }

              .company-logo {
                background: #1a237e;
                width: 64px;
                height: 64px;
                display: flex;
                align-items: center;
                justify-content: center;
                color: white;
                font-weight: bold;
                margin-bottom: 15px;
                border-radius: 4px;
              }

              .company-details {
                font-size: 11px;
                color: #546e7a;
                line-height: 1.8;
              }

              .report-header {
                text-align: right;
              }

              .report-title {
                color: #1565c0;
                font-size: 24px;
                font-weight: 700;
                margin: 30px 0;
                text-align: center;
                letter-spacing: 0.5px;
              }

              .report-metadata {
                font-size: 11px;
                color: #546e7a;
                text-align: right;
                margin-bottom: 40px;
              }

              /* Filter Section */
              .filter-section {
                background-color: #f8f9fa;
                border: 1px solid #e0e0e0;
                border-radius: 4px;
                padding: 15px;
                margin-bottom: 30px;
              }

              .filter-title {
                font-size: 12px;
                font-weight: 600;
                color: #37474f;
                margin-bottom: 10px;
                text-transform: uppercase;
              }

              .filter-table {
                width: 100%;
                margin-bottom: 0;
              }

              .filter-table td {
                padding: 8px 0;
                border: none;
                color: #546e7a;
              }

              .filter-table td:last-child {
                text-align: right;
                font-weight: 600;
              }

              /* Summary and Details Tables */
              .section-title {
                font-size: 14px;
                font-weight: 600;
                color: #37474f;
                margin: 25px 0 15px;
                text-transform: uppercase;
              }

              table {
                width: 100%;
                border-collapse: collapse;
                margin-bottom: 30px;
              }

              th {
                background-color: #f5f5f5;
                color: #37474f;
                font-weight: 600;
                text-align: left;
                padding: 12px;
                border: 1px solid #e0e0e0;
                font-size: 11px;
              }

              td {
                padding: 10px 12px;
                border: 1px solid #e0e0e0;
                color: #455a64;
                font-size: 11px;
              }

              .amount-cell {
                text-align: right;
                font-family: 'Courier New', monospace;
              }

              .reference-cell {
                font-family: 'Courier New', monospace;
                color: #0277bd;
              }

              .summary-table td {
                padding: 12px;
              }

              .summary-table td:first-child {
                width: 70%;
              }

              .total-row {
                font-weight: 600;
                background-color: #fafafa;
              }

              .total-row td {
                border-top: 2px solid #37474f;
              }

              /* Payment Methods Section */
              .payment-methods-section {
                background-color: #fff;
                border: 1px solid #e0e0e0;
                border-radius: 4px;
                padding: 15px;
                margin: 30px 0;
              }

              .payment-methods-table td {
                border: none;
                padding: 8px 0;
              }

              .payment-methods-table td:last-child {
                text-align: right;
                font-weight: 600;
              }

              /* Signature Section */
              .signature-section {
                display: flex;
                justify-content: space-between;
                margin-top: 60px;
                margin-bottom: 80px;
              }

              .signature-box {
                width: 45%;
              }

              .signature-line {
                border-bottom: 1px solid #90a4ae;
                margin-bottom: 8px;
                padding-top: 40px;
              }

              .signature-title {
                text-align: center;
                color: #546e7a;
                font-size: 11px;
                font-weight: 600;
              }

              /* Footer */
              .footer {
                position: absolute;
                bottom: 20mm;
                left: 20mm;
                right: 20mm;
                text-align: center;
                border-top: 1px solid #e0e0e0;
                padding-top: 15px;
              }

              .footer-text {
                color: #78909c;
                font-size: 10px;
                line-height: 1.6;
              }

              /* Print Optimizations */
              @media print {
                body {
                  -webkit-print-color-adjust: exact !important;
                  print-color-adjust: exact !important;
                }

                .page {
                  margin: 0;
                  padding: 20mm;
                }

                .company-logo {
                  print-color-adjust: exact;
                  -webkit-print-color-adjust: exact;
                }
              }
            </style>
          </head>
          <body>
            <div class="page">
              <!-- Company Header -->
              <div class="company-header">
                <div class="company-info">
                  <div class="company-logo">DEV</div>
                  <div class="company-details">
                    ${companyInfo.name}<br>
                    ${companyInfo.address}<br>
                    Tél: ${companyInfo.phone}<br>
                    Email: ${companyInfo.email}
                  </div>
                </div>
              </div>

              <!-- Report Title and Metadata -->
              <div class="report-title">
                RAPPORT DES PAIEMENTS FILTRÉS
              </div>
              <div class="report-metadata">
                Date d'édition: ${formatDate(new Date())}<br>
                Réf: RP-${new Date().toISOString().split("T")[0]}
              </div>

              <!-- Filter Criteria -->
              <div class="filter-section">
                <div class="filter-title">CRITÈRES DE FILTRAGE</div>
                <table class="filter-table">
                  <tr>
                    <td>Client</td>
                    <td>${filterInfo.client ? `${filterInfo.client.nom} ${filterInfo.client.prenom}` : "Tous les clients"}</td>
                  </tr>
                  <tr>
                    <td>Période</td>
                    <td>${
                      filterInfo.dateRange.start && filterInfo.dateRange.end
                        ? `Du ${formatDate(filterInfo.dateRange.start)} au ${formatDate(filterInfo.dateRange.end)}`
                        : "Toutes les dates"
                    }</td>
                  </tr>
                </table>
              </div>

              <!-- Summary Section -->
              <div class="section-title">RÉCAPITULATIF</div>
              <table class="summary-table">
                <tr>
                  <td>Nombre total de paiements</td>
                  <td class="amount-cell">${payments.length}</td>
                </tr>
                <tr>
                  <td>Montant total encaissé</td>
                  <td class="amount-cell">${totalAmount.toLocaleString(
                    "fr-FR",
                    {
                      style: "currency",
                      currency: "MAD",
                      minimumFractionDigits: 2,
                    }
                  )}</td>
                </tr>
              </table>

              <!-- Payments Detail Section -->
              <div class="section-title">DÉTAIL DES PAIEMENTS</div>
              <table>
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>N° Vente</th>
                    <th>N° Paiement</th>
                    <th>Mode</th>
                    <th>Référence</th>
                    <th style="text-align: right">Montant</th>
                  </tr>
                </thead>
                <tbody>
                  ${payments
                    .map(
                      (payment) => `
                    <tr>
                      <td>${payment.nom} ${payment.prenom}</td>
                      <td class="reference-cell">${payment.numero_vente}</td>
                      <td class="reference-cell">${payment.numero_paiement}</td>
                      <td>${payment.mode_paiement}</td>
                      <td class="reference-cell">${payment.reference_paiement || "-"}</td>
                      <td class="amount-cell">${parseFloat(
                        payment.montant
                      ).toLocaleString("fr-FR", {
                        style: "currency",
                        currency: "MAD",
                        minimumFractionDigits: 2,
                      })}</td>
                    </tr>
                  `
                    )
                    .join("")}
                  <tr class="total-row">
                    <td colspan="5">Total</td>
                    <td class="amount-cell">${totalAmount.toLocaleString(
                      "fr-FR",
                      {
                        style: "currency",
                        currency: "MAD",
                        minimumFractionDigits: 2,
                      }
                    )}</td>
                  </tr>
                </tbody>
              </table>

              <!-- Payment Methods Summary -->
              <div class="payment-methods-section">
                <div class="filter-title">RÉPARTITION PAR MODE DE PAIEMENT</div>
                <table class="payment-methods-table">
                  ${Object.entries(
                    payments.reduce((acc, payment) => {
                      const mode = payment.mode_paiement || "Non spécifié";
                      acc[mode] =
                        (acc[mode] || 0) + parseFloat(payment.montant || 0);
                      return acc;
                    }, {})
                  )
                    .map(
                      ([mode, amount]) => `
                    <tr>
                      <td>${mode}</td>
                      <td>${amount.toLocaleString("fr-FR", {
                        style: "currency",
                        currency: "MAD",
                        minimumFractionDigits: 2,
                      })}</td>
                    </tr>
                  `
                    )
                    .join("")}
                </table>
              </div>

              <!-- Signatures Section -->
              <div class="signature-section">
                <div class="signature-box">
                  <div class="signature-line"></div>
                  <div class="signature-title">Préparé par</div>
                </div>
                <div class="signature-box">
                  <div class="signature-line"></div>
                  <div class="signature-title">Approuvé par</div>
                </div>
              </div>

              <!-- Footer -->
              <div class="footer">
                <div class="footer-text">
                  Ce rapport est généré automatiquement et ne nécessite pas de signature électronique<br>
                  ${companyInfo.name} • ${companyInfo.address} • Tél: ${companyInfo.phone}
                </div>
              </div>
            </div>
          </body>
        </html>
      `);

      printWindow.document.close();
      printWindow.focus();

      setTimeout(() => {
        printWindow.print();
        printWindow.close();
        setIsPrinting(false);
      }, 250);
    } catch (error) {
      console.error("Print error:", error);
      alert("Error generating PDF: " + error.message);
      setIsPrinting(false);
    }
  };

  return (
    <Box>
      <Button
        variant="contained"
        color="secondary"
        startIcon={
          isPrinting ? <CircularProgress size={20} /> : <PictureAsPdfIcon />
        }
        onClick={handlePrint}
        disabled={isPrinting}
      >
        {isPrinting ? "Génération du PDF..." : "Télécharger PDF"}
      </Button>
    </Box>
  );
};

export default FilteredPaymentsReport;
